import React, { useState, useRef, useEffect, useCallback } from 'react';
import imagePerformanceMonitor from '../../utils/imagePerformance';
import { getBestGatewayUrl, isIPFSUrl, getGatewayUrlsForFallback } from '../../utils/ipfsGateway';

// Image cache to store loaded images - shared across all components
export const imageCache = new Map();
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

// Export cache functions for use in other components
export const getCachedImage = (url) => {
  const cached = imageCache.get(url);
  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
    return cached.src;
  }
  return null;
};

export const cacheImage = (url, src) => {
  imageCache.set(url, {
    src,
    timestamp: Date.now()
  });
};

// Preload critical images
const preloadQueue = new Set();
const MAX_PRELOAD = 5; // Limit concurrent preloads

const OptimizedImage = ({
  src,
  alt = "Property image",
  className = "",
  fallbackSrc = null,
  priority = false,
  lazy = true,
  onLoad,
  onError,
  placeholder = true,
  blurDataURL = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMjUgNzVIMTc1VjEyNUgxMjVWNzVaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0xMzcuNSAxMDAuNUwxNDIuNSAxMDUuNUwxNTcuNSA5MC41TDE2Mi41IDk1LjVMMTQ3LjUgMTEwLjVMMTM3LjUgMTAwLjVaIiBmaWxsPSIjNkI3MjgwIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOUNBM0FGIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiPkxvYWRpbmcuLi48L3RleHQ+Cjwvc3ZnPgo=",
  progressive = true,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(null); // Start with null to show skeleton
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isInView, setIsInView] = useState(!lazy || priority);
  const [lowQualitySrc, setLowQualitySrc] = useState(null);
  const [showLowQuality, setShowLowQuality] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Check if image is cached (using exported function)
  const getCachedImageLocal = useCallback((url) => {
    return getCachedImage(url);
  }, []);

  // Cache loaded image (using exported function)
  const cacheImageLocal = useCallback((url, src) => {
    cacheImage(url, src);
  }, []);

  // Preload image with optimized retry logic (max 3 retries) and gateway fallback
  const preloadImage = useCallback((url, retryCount = 0, maxRetries = 3, abortController = null) => {
    return new Promise((resolve, reject) => {
      // Check cache first before attempting to load
      const cached = getCachedImageLocal(url);
      if (cached) {
        resolve(cached);
        return;
      }

      // Check if request was aborted
      if (abortController?.signal.aborted) {
        reject(new Error('Request aborted'));
        return;
      }

      const trackingData = imagePerformanceMonitor.trackImageStart(url);
      const img = new Image();
      let timeout;
      let isResolved = false;
      
      // Reduced timeout: 5 seconds for first attempt, 8 seconds for retries
      const timeoutDuration = retryCount === 0 ? 5000 : 8000;
      
      // Cleanup function
      const cleanup = () => {
        clearTimeout(timeout);
        img.onload = null;
        img.onerror = null;
        img.src = ''; // Cancel image load
      };

      timeout = setTimeout(() => {
        if (isResolved) return;
        cleanup();
        imagePerformanceMonitor.trackImageError(url, new Error('Image load timeout'));
        
        // Retry if we haven't exceeded max retries
        if (retryCount < maxRetries && !abortController?.signal.aborted) {
          const delay = Math.min(300 * (retryCount + 1), 1000); // Max 1s delay
          setTimeout(() => {
            preloadImage(url, retryCount + 1, maxRetries, abortController)
              .then(resolve)
              .catch(reject);
          }, delay);
        } else {
          reject(new Error('Image load timeout after retries'));
        }
      }, timeoutDuration);

      img.onload = () => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        // Cache the successfully loaded image
        cacheImageLocal(url, url);
        imagePerformanceMonitor.trackImageEnd(trackingData, false);
        resolve(url);
      };

      img.onerror = () => {
        if (isResolved) return;
        cleanup();
        imagePerformanceMonitor.trackImageError(url, new Error('Image load failed'));
        
        // Retry if we haven't exceeded max retries
        if (retryCount < maxRetries && !abortController?.signal.aborted) {
          const delay = Math.min(300 * (retryCount + 1), 1000); // Max 1s delay
          setTimeout(() => {
            preloadImage(url, retryCount + 1, maxRetries, abortController)
              .then(resolve)
              .catch(reject);
          }, delay);
        } else {
          reject(new Error('Image load failed after retries'));
        }
      };

      // Add CORS
      img.crossOrigin = 'anonymous';
      
      // Determine which URL to use
      let imageUrl = url;
      
      // For IPFS URLs, try best gateway first, then fallback to other gateways on retry
      if (isIPFSUrl(url)) {
        if (retryCount === 0) {
          // First attempt: use best gateway (usually Pinata)
          imageUrl = getBestGatewayUrl(url);
        } else {
          // Retry: try next gateway in the list
          const gatewayUrls = getGatewayUrlsForFallback(url);
          const gatewayIndex = Math.min(retryCount, gatewayUrls.length - 1);
          imageUrl = gatewayUrls[gatewayIndex] || url;
        }
      }
      
      // Set image source
      if (!isResolved && !abortController?.signal.aborted) {
        img.src = imageUrl;
      }
    });
  }, [cacheImageLocal, getCachedImageLocal]);

  // Load image with fallback
  const loadImage = useCallback(async (url) => {
    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    try {
      // Check cache first
      const cached = getCachedImageLocal(url);
      if (cached) {
        const trackingData = imagePerformanceMonitor.trackImageStart(url);
        setImageSrc(cached);
        setIsLoaded(true);
        imagePerformanceMonitor.trackImageEnd(trackingData, true);
        onLoad?.(cached);
        return;
      }

      // Preload with timeout and retry logic (max 3 retries)
      const loadedUrl = await preloadImage(url, 0, 3, abortControllerRef.current);
      
      // Check if request was aborted
      if (signal.aborted) {
        return;
      }
      
      setImageSrc(loadedUrl);
      setIsLoaded(true);
      setShowLowQuality(false); // Hide low-quality image when high-quality loads
      onLoad?.(loadedUrl);
    } catch (error) {
      // Don't handle abort errors
      if (error.name === 'AbortError' || signal.aborted) {
        return;
      }
      
      console.warn('Failed to load image after retries:', url, error);
      setIsError(true);
      // Only use fallback if it's not the logo
      if (fallbackSrc && !fallbackSrc.includes('logoForQRcode')) {
        setImageSrc(fallbackSrc);
        setIsLoaded(false); // Keep skeleton showing until fallback loads
      }
      onError?.(error);
    }
  }, [getCachedImageLocal, preloadImage, fallbackSrc, onLoad, onError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || priority || isInView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image comes into view
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
      observerRef.current = observer;
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [lazy, priority, isInView]);

  // Load low-quality image first for progressive loading
  useEffect(() => {
    if (isInView && src && progressive && !lowQualitySrc) {
      const lowQualityUrl = getOptimizedImageUrl(src, { quality: 20, width: 200, height: 150 });
      setLowQualitySrc(lowQualityUrl);
      setShowLowQuality(true);
    }
  }, [isInView, src, progressive, lowQualitySrc]);

  // Load image when in view
  useEffect(() => {
    if (isInView && src && !imageSrc) {
      loadImage(src);
    }
  }, [isInView, src, imageSrc, loadImage]);

  // Priority preloading
  useEffect(() => {
    if (priority && src && !preloadQueue.has(src)) {
      preloadQueue.add(src);
      loadImage(src);
    }
  }, [priority, src, loadImage]);

  // Extract rounded classes from className
  const roundedClass = className?.split(' ').find(c => c.includes('rounded')) || '';

  // Image skeleton component with top-to-bottom shimmer effect
  const ImageSkeleton = () => (
    <div 
      className={`absolute inset-0 overflow-hidden ${roundedClass}`}
      style={{
        backgroundColor: '#f0f0f0',
        position: 'relative',
      }}
    >
      {/* Base gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, #f0f0f0 0%, #e8e8e8 25%, #f0f0f0 50%, #e8e8e8 75%, #f0f0f0 100%)',
          backgroundSize: '100% 400%',
          animation: 'skeletonGradient 2s ease-in-out infinite',
        }}
      />
      
      {/* Shimmer effect - sweeps from top to bottom */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.6) 30%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.6) 70%, transparent 100%)',
          backgroundSize: '100% 200%',
          animation: 'shimmerTopToBottom 1.8s ease-in-out infinite',
        }}
      />
      
      <style>{`
        @keyframes shimmerTopToBottom {
          0% {
            transform: translateY(-100%);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(100%);
            opacity: 0;
          }
        }
        @keyframes skeletonGradient {
          0%, 100% {
            background-position: 0% 0%;
          }
          50% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </div>
  );

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${className}`} {...props}>
      {/* Low-quality placeholder for progressive loading */}
      {progressive && showLowQuality && lowQualitySrc && !isLoaded && (
        <img
          src={lowQualitySrc}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover filter blur-sm ${roundedClass}`}
          style={{ imageRendering: 'pixelated' }}
        />
      )}
      
      {/* Loading skeleton - shows until image is loaded */}
      {placeholder && !isLoaded && !showLowQuality && (
        <ImageSkeleton />
      )}
      
      {/* High-quality image */}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${roundedClass}`}
          style={{ position: 'relative', zIndex: 1 }}
          onLoad={() => {
            setIsLoaded(true);
            setShowLowQuality(false);
          }}
          onError={() => {
            // Don't set fallbackSrc if it's the logo - keep retrying or show placeholder
            if (fallbackSrc && !fallbackSrc.includes('logoForQRcode')) {
              setImageSrc(fallbackSrc);
              setIsLoaded(false); // Keep skeleton showing while trying fallback
            } else {
              setIsError(true);
              setIsLoaded(false); // Keep skeleton showing on error
            }
          }}
        />
      )}
      
      {/* Error state - Show generic placeholder instead of logo */}
      {isError && !fallbackSrc && (
        <div className={`absolute inset-0 bg-gray-200 flex items-center justify-center overflow-hidden ${roundedClass}`} style={{ zIndex: 2 }}>
          <div className="text-gray-500 text-center">
            <div className="text-4xl mb-2">🏠</div>
            <div className="text-sm">Image unavailable</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Hook for preloading multiple images
export const useImagePreloader = () => {
  const preloadImages = useCallback(async (urls, maxConcurrent = 3) => {
    const chunks = [];
    for (let i = 0; i < urls.length; i += maxConcurrent) {
      chunks.push(urls.slice(i, i + maxConcurrent));
    }

    for (const chunk of chunks) {
      await Promise.allSettled(
        chunk.map(url => {
          if (!preloadQueue.has(url)) {
            preloadQueue.add(url);
            return new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = () => resolve(url);
              img.onerror = () => reject(new Error('Failed to load'));
              img.crossOrigin = 'anonymous';
              img.src = url;
            });
          }
          return Promise.resolve(url);
        })
      );
    }
  }, []);

  return { preloadImages };
};

// Utility function to get optimized image URL
export const getOptimizedImageUrl = (originalUrl, options = {}) => {
  // Return early if originalUrl is falsy or not a string
  if (!originalUrl || typeof originalUrl !== 'string') {
    return originalUrl || null;
  }
  
  // If it's already a data URL or local image, return as is
  if (originalUrl.startsWith('data:') || originalUrl.startsWith('/')) {
    return originalUrl;
  }

  // For IPFS/Pinata URLs, you can add query parameters for optimization
  // This is a placeholder - you might want to use a service like Cloudinary or similar
  const { width, height, quality = 80, format = 'webp' } = options;
  
  // Example: Add optimization parameters to Pinata URLs
  if (originalUrl.includes('pinata.cloud')) {
    try {
      const url = new URL(originalUrl);
      if (width) url.searchParams.set('w', width);
      if (height) url.searchParams.set('h', height);
      url.searchParams.set('q', quality);
      url.searchParams.set('f', format);
      return url.toString();
    } catch (error) {
      // If URL parsing fails, return original URL
      console.warn('Failed to parse URL for optimization:', originalUrl);
      return originalUrl;
    }
  }

  return originalUrl;
};

export default OptimizedImage;
